import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { Loader2, Plus, ShieldCheck } from "lucide-react";

export function CapitalPortfolio() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/capital-portfolio/overview").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Capital Portfolio Intelligence"
                title="Stage-Gate Governance · CPI · SPI · NPV · IRR"
                description="Executive view of every capital project — spend, schedule, value and AI-recommended actions. Every decision is stage-gated and audit-signed." />
            <KpiGrid kpis={data?.kpis || []} accent="cobalt" cols={6} />
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="Portfolio Composition" accent="cobalt">
                    <PortfolioBreakdown projects={data?.projects || []} />
                </Panel>
                <Panel title="Stage-Gate Distribution" accent="copper">
                    <GateBreakdown projects={data?.projects || []} />
                </Panel>
                <Panel title="Governance" accent="purple">
                    <div className="text-xs kcc-text-silver space-y-2 leading-relaxed">
                        <div className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-kcc-copper-light mt-0.5" /><div>All projects follow the KCC Stage-Gate framework (5 gates) with independent Owner's Engineer sign-off.</div></div>
                        <div>· Portfolio approved by the Executive Committee quarterly and by the Board annually.</div>
                        <div>· Capital reallocations &gt; $5M require Board endorsement.</div>
                        <div>· NPV, IRR and payback are recomputed monthly with latest LME curves and energy tariff assumptions.</div>
                        <div>· Every decision is audit-logged in the Executive Decision Register.</div>
                    </div>
                </Panel>
            </div>
            <Panel title="Capital Projects Register" accent="cobalt">
                <DataTable
                    columns={[
                        { key: "ref", label: "Ref" },
                        { key: "name", label: "Project" },
                        { key: "category", label: "Category" },
                        { key: "sponsor", label: "Sponsor" },
                        { key: "gate", label: "Stage-Gate" },
                        { key: "budget_musd", label: "Budget $M", render: (r) => `$${r.budget_musd.toFixed(1)}M` },
                        { key: "spent_musd", label: "Spent $M", render: (r) => `$${r.spent_musd.toFixed(1)}M` },
                        { key: "progress_pct", label: "Progress", render: (r) => `${r.progress_pct}%` },
                        { key: "cpi", label: "CPI", render: (r) => <StatusChip status={r.cpi >= 0.95 ? "normal" : "warning"}>{r.cpi.toFixed(2)}</StatusChip> },
                        { key: "spi", label: "SPI", render: (r) => <StatusChip status={r.spi >= 0.95 ? "normal" : "warning"}>{r.spi.toFixed(2)}</StatusChip> },
                        { key: "npv_musd", label: "NPV $M", render: (r) => `$${r.npv_musd.toFixed(1)}M` },
                        { key: "irr_pct", label: "IRR", render: (r) => `${r.irr_pct}%` },
                        { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        { key: "top_risk", label: "Top Risk" },
                        { key: "ai_recommendation", label: "AI Recommendation" },
                    ]}
                    rows={data?.projects || []}
                    testid="capital-portfolio-table"
                />
            </Panel>
            <div className="text-[11px] kcc-text-dim border-t border-white/5 pt-3">
                Fictional demonstration data — Kamoto Copper Company capital plan illustrated for KCC360 executive demonstration.
            </div>
        </div>
    );
}

function PortfolioBreakdown({ projects }) {
    if (!projects.length) return <div className="kcc-text-dim text-sm">Loading…</div>;
    const groups = {};
    for (const p of projects) {
        groups[p.category] = (groups[p.category] || 0) + p.budget_musd;
    }
    const entries = Object.entries(groups).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
    const palette = ["#b87333", "#2d68c4", "#5c8ee6", "#7fb069", "#c98a3c", "#8b5cf6", "#e0a458"];
    return (
        <div className="space-y-2">
            {entries.map(([cat, val], i) => (
                <div key={cat} data-testid={`portfolio-cat-${cat.toLowerCase().replace(/\s+/g,'-')}`}>
                    <div className="flex justify-between text-xs mb-1">
                        <span className="kcc-text-silver">{cat}</span>
                        <span className="font-mono-kcc text-white">${val.toFixed(0)}M</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full" style={{ width: `${(val / total) * 100}%`, background: palette[i % palette.length] }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

function GateBreakdown({ projects }) {
    if (!projects.length) return <div className="kcc-text-dim text-sm">Loading…</div>;
    const groups = {};
    for (const p of projects) groups[p.gate] = (groups[p.gate] || 0) + 1;
    const ordered = ["Gate 1 · Concept", "Gate 2 · Feasibility", "Gate 3 · Definition", "Gate 4 · Execution", "Gate 5 · Handover"];
    return (
        <div className="space-y-2">
            {ordered.map((g) => (
                <div key={g} className="flex items-center gap-3">
                    <div className="kcc-label w-40 truncate">{g}</div>
                    <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-kcc-copper" style={{ width: `${((groups[g] || 0) / projects.length) * 100}%` }} />
                    </div>
                    <div className="text-xs font-mono-kcc text-white w-6 text-right">{groups[g] || 0}</div>
                </div>
            ))}
        </div>
    );
}


export function DecisionRegister() {
    const [data, setData] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ title: "", category: "Operational", owner: "CEO", rationale: "", benefit_musd: 0, risk_score: 3, ai_recommendation: "Proceed with conditions" });

    async function refresh() {
        const { data } = await api.get("/decisions/register");
        setData(data);
    }
    useEffect(() => { refresh(); }, []);

    async function submit() {
        if (!form.title.trim()) return;
        setBusy(true);
        try {
            await api.post("/decisions/log", form);
            setShowForm(false);
            setForm({ ...form, title: "", rationale: "" });
            await refresh();
        } finally { setBusy(false); }
    }

    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Executive Decision Register"
                title="Every Decision · Every Owner · Every Outcome"
                description="Chronological audit-signed log of all executive decisions — with AI recommendation, benefit tracking and board escalation. Non-repudiation by design." />
            <div className="flex items-center justify-between">
                <KpiGrid kpis={data?.kpis || []} accent="purple" cols={6} />
            </div>
            <div className="flex justify-end">
                <button onClick={() => setShowForm((s) => !s)} className="inline-flex items-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light text-white rounded-md px-4 py-2 text-sm" data-testid="log-decision-btn">
                    <Plus className="w-4 h-4" /> Log new decision
                </button>
            </div>
            {showForm && (
                <Panel title="Log Executive Decision" accent="copper">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="text-xs kcc-text-silver">Title
                            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="decision-title" />
                        </label>
                        <label className="text-xs kcc-text-silver">Category
                            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="decision-category">
                                {["Operational", "Capital", "Commercial", "ESG", "Safety", "Cyber", "Social", "Governance"].map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="text-xs kcc-text-silver">Owner
                            <select value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="mt-1 w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="decision-owner">
                                {["CEO", "COO", "CFO", "CISO", "Board", "Mine Manager", "Plant Manager", "HSE Manager"].map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="text-xs kcc-text-silver">AI Recommendation
                            <select value={form.ai_recommendation} onChange={(e) => setForm({ ...form, ai_recommendation: e.target.value })} className="mt-1 w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="decision-ai">
                                {["Proceed", "Proceed with conditions", "Defer for further analysis", "Approve subject to Board endorsement"].map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="text-xs kcc-text-silver">Expected benefit ($M)
                            <input type="number" step="0.1" value={form.benefit_musd} onChange={(e) => setForm({ ...form, benefit_musd: parseFloat(e.target.value || 0) })} className="mt-1 w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="decision-benefit" />
                        </label>
                        <label className="text-xs kcc-text-silver">Residual risk score (1–5)
                            <input type="number" min="1" max="5" value={form.risk_score} onChange={(e) => setForm({ ...form, risk_score: parseInt(e.target.value || 3) })} className="mt-1 w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="decision-risk" />
                        </label>
                        <label className="text-xs kcc-text-silver md:col-span-2">Rationale
                            <textarea value={form.rationale} onChange={(e) => setForm({ ...form, rationale: e.target.value })} rows={3} className="mt-1 w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="decision-rationale" />
                        </label>
                    </div>
                    <div className="flex justify-end mt-4">
                        <button onClick={submit} disabled={busy || !form.title.trim()} className="inline-flex items-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light disabled:opacity-40 text-white rounded-md px-4 py-2 text-sm" data-testid="decision-submit">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Sign & log
                        </button>
                    </div>
                </Panel>
            )}
            <Panel title="Executive Decisions · Chronological · Audit-signed" accent="purple">
                <DataTable
                    columns={[
                        { key: "ref", label: "Ref" },
                        { key: "date", label: "Date", render: (r) => r.date?.slice(0, 10) || "—" },
                        { key: "title", label: "Decision" },
                        { key: "category", label: "Category" },
                        { key: "owner", label: "Owner" },
                        { key: "ai_recommendation", label: "AI", render: (r) => <StatusChip status={statusToChip(r.ai_recommendation)}>{r.ai_recommendation}</StatusChip> },
                        { key: "benefit_musd", label: "Benefit $M", render: (r) => `$${(r.benefit_musd || 0).toFixed(1)}M` },
                        { key: "board_level", label: "Board?", render: (r) => r.board_level ? <StatusChip status="info">Board</StatusChip> : "—" },
                        { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        { key: "audit_signed", label: "Signed", render: (r) => r.audit_signed !== false ? <StatusChip status="normal">Yes</StatusChip> : "—" },
                    ]}
                    rows={data?.decisions || []}
                    testid="decision-register-table"
                />
            </Panel>
        </div>
    );
}
